/**
 * built-in dynamic variable providers.
 * each key maps to a function that generates a value on demand.
 * these are resolved when the user uses {{$providerName}} syntax.
 */

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const FIRST_NAMES = [
  "Alice",
  "Bob",
  "Charlie",
  "Diana",
  "Eve",
  "Frank",
  "Grace",
  "Henry",
  "Ivy",
  "Jack",
  "Karen",
  "Leo",
  "Mia",
  "Noah",
  "Olivia",
  "Paul",
  "Quinn",
  "Rachel",
  "Sam",
  "Tina",
  "Uma",
  "Victor",
  "Wendy",
  "Xander",
  "Yara",
  "Zoe",
  "Aaron",
  "Beth",
  "Carl",
  "Donna",
];

const LAST_NAMES = [
  "Smith",
  "Johnson",
  "Williams",
  "Brown",
  "Jones",
  "Garcia",
  "Miller",
  "Davis",
  "Martinez",
  "Wilson",
  "Anderson",
  "Taylor",
  "Thomas",
  "Moore",
  "Jackson",
  "Martin",
  "Lee",
  "Perez",
  "Thompson",
  "White",
  "Harris",
  "Sanchez",
  "Clark",
  "Lewis",
  "Robinson",
];

const DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "example.com",
  "test.io",
  "mail.com",
  "proton.me",
  "icloud.com",
];

const TLDS = ["com", "net", "org", "io", "dev", "app", "co"];

const WORDS = [
  "alpha",
  "bravo",
  "charlie",
  "delta",
  "echo",
  "foxtrot",
  "golf",
  "hotel",
  "india",
  "juliet",
  "kilo",
  "lima",
  "mike",
  "november",
  "oscar",
  "papa",
  "quebec",
  "romeo",
  "sierra",
  "tango",
  "uniform",
  "victor",
  "whiskey",
  "xray",
  "yankee",
  "zulu",
];

const CITIES = [
  "New York",
  "Los Angeles",
  "Chicago",
  "Houston",
  "Phoenix",
  "Philadelphia",
  "San Antonio",
  "San Diego",
  "Dallas",
  "San Jose",
  "London",
  "Berlin",
  "Paris",
  "Tokyo",
  "Sydney",
  "Toronto",
  "Mumbai",
  "Singapore",
  "Dubai",
];

const COUNTRIES = [
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Japan",
  "India",
  "Brazil",
  "Mexico",
  "Italy",
  "Spain",
  "Netherlands",
  "Sweden",
  "Norway",
  "South Korea",
  "Singapore",
];

const JOB_TITLES = [
  "Software Engineer",
  "Product Manager",
  "Data Scientist",
  "UX Designer",
  "DevOps Engineer",
  "Marketing Manager",
  "Sales Representative",
  "CEO",
  "CTO",
  "Frontend Developer",
  "Backend Developer",
  "QA Engineer",
  "Business Analyst",
  "Cloud Architect",
  "Security Engineer",
];

const COMPANY_NAMES = [
  "Acme Corp",
  "Globex",
  "Initech",
  "Umbrella Corp",
  "Soylent Corp",
  "Massive Dynamic",
  "Oceanic Airlines",
  "Stark Industries",
  "Wayne Enterprises",
  "Vandelay Industries",
  "Sterling Cooper",
  "Bluth Company",
  "Dunder Mifflin",
];

const COLORS = [
  "red",
  "blue",
  "green",
  "yellow",
  "orange",
  "purple",
  "pink",
  "cyan",
  "magenta",
  "lime",
  "indigo",
  "violet",
  "teal",
  "amber",
];

const LOREM_WORDS = [
  "lorem",
  "ipsum",
  "dolor",
  "sit",
  "amet",
  "consectetur",
  "adipiscing",
  "elit",
  "sed",
  "do",
  "eiusmod",
  "tempor",
  "incididunt",
  "ut",
  "labore",
  "et",
  "dolore",
  "magna",
  "aliqua",
  "enim",
  "ad",
  "minim",
  "veniam",
];

const uuid = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const randomChars = (len, chars) => {
  let result = "";
  for (let i = 0; i < len; i++) result += chars[Math.floor(Math.random() * chars.length)];
  return result;
};

export const BUILTIN_PROVIDERS = {
  timestamp: () => Date.now().toString(),
  isoTimestamp: () => new Date().toISOString(),

  guid: () => uuid(),
  uuid: () => uuid(),

  randomInt: (min = "0", max = "1000") => {
    const lo = Math.min(Number(min), Number(max));
    const hi = Math.max(Number(min), Number(max));
    return String(Math.floor(lo + Math.random() * (hi - lo + 1)));
  },
  randomFloat: (min = "0", max = "1", decimals = "2") => {
    const val = Number(min) + Math.random() * (Number(max) - Number(min));
    return val.toFixed(Number(decimals));
  },
  randomBoolean: () => String(Math.random() < 0.5),

  randomString: (len = "16") => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    return randomChars(Number(len) || 16, chars);
  },
  randomAlphaNumeric: () =>
    randomChars(1, "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"),
  randomHexColor: () => "#" + randomChars(6, "0123456789abcdef"),
  randomColor: () => pick(COLORS),
  randomWord: () => pick(WORDS),
  randomWords: () => `${pick(WORDS)} ${pick(WORDS)} ${pick(WORDS)}`,
  randomLoremWord: () => pick(LOREM_WORDS),
  randomLoremSentence: () => {
    const len = 6 + Math.floor(Math.random() * 8);
    return Array.from({ length: len }, () => pick(LOREM_WORDS)).join(" ") + ".";
  },

  randomFirstName: () => pick(FIRST_NAMES),
  randomLastName: () => pick(LAST_NAMES),
  randomFullName: () => `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
  randomUserName: () => `${pick(FIRST_NAMES).toLowerCase()}${Math.floor(Math.random() * 999)}`,
  randomEmail: () => {
    const name = `${pick(FIRST_NAMES).toLowerCase()}.${pick(LAST_NAMES).toLowerCase()}`;
    return `${name}@${pick(DOMAINS)}`;
  },
  randomPhoneNumber: () => {
    const n = () => Math.floor(Math.random() * 10);
    return `+1 (${n()}${n()}${n()}) ${n()}${n()}${n()}-${n()}${n()}${n()}${n()}`;
  },
  randomJobTitle: () => pick(JOB_TITLES),
  randomCompanyName: () => pick(COMPANY_NAMES),

  randomUrl: () => `https://www.${pick(WORDS)}.${pick(TLDS)}`,
  randomDomainName: () => `${pick(WORDS)}.${pick(TLDS)}`,
  randomDomainSuffix: () => pick(TLDS),
  randomIP: () => Array.from({ length: 4 }, () => Math.floor(Math.random() * 256)).join("."),
  randomIPv6: () => Array.from({ length: 8 }, () => randomChars(4, "0123456789abcdef")).join(":"),
  randomMACAddress: () =>
    Array.from({ length: 6 }, () => randomChars(2, "0123456789ABCDEF")).join(":"),
  randomProtocol: () => pick(["http", "https", "ftp", "ssh", "ws", "wss"]),

  randomCity: () => pick(CITIES),
  randomCountry: () => pick(COUNTRIES),
  randomCountryCode: () => randomChars(2, "ABCDEFGHIJKLMNOPQRSTUVWXYZ"),
  randomStreetAddress: () =>
    `${Math.floor(Math.random() * 9999) + 1} ${pick(WORDS).charAt(0).toUpperCase() + pick(WORDS).slice(1)} St`,
  randomZipCode: () => randomChars(5, "0123456789"),

  randomAbbreviation: () => randomChars(3, "ABCDEFGHIJKLMNOPQRSTUVWXYZ"),
};

export const BUILTIN_PROVIDER_METADATA = [
  { name: "$timestamp", description: "Current Unix timestamp (ms)", category: "time" },
  { name: "$isoTimestamp", description: "Current ISO 8601 date/time", category: "time" },

  { name: "$guid", description: "Random UUID v4", category: "id" },
  { name: "$uuid", description: "Random UUID v4", category: "id" },

  { name: "$randomInt", description: "Random integer (min, max)", category: "number" },
  { name: "$randomFloat", description: "Random float (min, max, decimals)", category: "number" },
  { name: "$randomBoolean", description: "Random true or false", category: "number" },

  { name: "$randomString", description: "Random alphanumeric string (len)", category: "string" },
  {
    name: "$randomAlphaNumeric",
    description: "Single random alphanumeric char",
    category: "string",
  },
  { name: "$randomHexColor", description: "Random hex color (#rrggbb)", category: "string" },
  { name: "$randomColor", description: "Random color name", category: "string" },
  { name: "$randomWord", description: "Random word", category: "string" },
  { name: "$randomWords", description: "Random 3-word phrase", category: "string" },
  { name: "$randomLoremWord", description: "Random Lorem Ipsum word", category: "string" },
  { name: "$randomLoremSentence", description: "Random Lorem Ipsum sentence", category: "string" },
  { name: "$randomAbbreviation", description: "Random 3-letter abbreviation", category: "string" },

  { name: "$randomFirstName", description: "Random first name", category: "person" },
  { name: "$randomLastName", description: "Random last name", category: "person" },
  { name: "$randomFullName", description: "Random full name", category: "person" },
  { name: "$randomUserName", description: "Random username", category: "person" },
  { name: "$randomEmail", description: "Random email address", category: "person" },
  { name: "$randomPhoneNumber", description: "Random phone number", category: "person" },
  { name: "$randomJobTitle", description: "Random job title", category: "person" },
  { name: "$randomCompanyName", description: "Random company name", category: "person" },

  { name: "$randomUrl", description: "Random URL", category: "internet" },
  { name: "$randomDomainName", description: "Random domain name", category: "internet" },
  { name: "$randomDomainSuffix", description: "Random TLD (com, net, etc.)", category: "internet" },
  { name: "$randomIP", description: "Random IPv4 address", category: "internet" },
  { name: "$randomIPv6", description: "Random IPv6 address", category: "internet" },
  { name: "$randomMACAddress", description: "Random MAC address", category: "internet" },
  { name: "$randomProtocol", description: "Random protocol (http, https…)", category: "internet" },

  { name: "$randomCity", description: "Random city name", category: "location" },
  { name: "$randomCountry", description: "Random country name", category: "location" },
  { name: "$randomCountryCode", description: "Random 2-letter country code", category: "location" },
  { name: "$randomStreetAddress", description: "Random street address", category: "location" },
  { name: "$randomZipCode", description: "Random zip code", category: "location" },
];
